import type { SerializedNode, SerializedNodes } from '@craftjs/core';
import { instanceToPlain, plainToClass } from 'class-transformer';
import { IsNumber, Max, IsOptional, IsDefined, IsString } from 'class-validator';
import { SerializedNodeDto } from '@builder4/shared/dto/craft/SerializedNodeDto';
import { MigratableJson } from '../../migrations/craft/MigratableJson';
import { ValidateNestedRecord } from '@builder4/shared/dto/craft/validators/ValidateNestedRecord';
import { CURRENT_SCHEMA_VERSION } from '@builder4/shared/migrations/craft/registry';
import type { BackgroundImageMode, BackgroundPosition } from '../../types/layout';

/**
 * Full page format: ROOT node at top level with other nodes spread
 * Example: { ROOT: {...}, nodeId1: {...}, version?: number }
 */
export type VersionedSerializedNodes = SerializedNodes & { version?: number; ROOT: SerializedNode };

/**
 * Section format: rootNodeId references which node is root, all nodes in nodes record
 * Example: { rootNodeId: 'abc', nodes: { abc: {...}, nodeId1: {...} }, version?: number }
 */
export type VersionedSectionNodes = {
  version?: number;
  rootNodeId: string;
  nodes: SerializedNodes;
};

export type CraftJsonInput = VersionedSerializedNodes | VersionedSectionNodes;

export class CraftJsonDto extends MigratableJson {
  @IsOptional()
  @IsNumber()
  @Max(CURRENT_SCHEMA_VERSION)
  version?: number;

  @IsDefined()
  @IsString()
  rootNodeId: string;

  @ValidateNestedRecord()
  nodes: Record<string, SerializedNodeDto>;

  private isSectionFormat: boolean;

  private constructor(
    rootNodeId: string,
    nodes: Record<string, SerializedNodeDto>,
    isSectionFormat: boolean,
    version?: number,
  ) {
    super();
    this.rootNodeId = rootNodeId;
    this.nodes = nodes;
    this.isSectionFormat = isSectionFormat;
    this.version = version;
  }

  /**
   * Create a CraftJsonDto from full page format
   * Input: { ROOT: {...}, nodeId1: {...}, version?: number }
   */
  static fromPage(data: VersionedSerializedNodes): CraftJsonDto {
    const { ROOT, version, ...rest } = data;
    const nodes: Record<string, SerializedNodeDto> = {
      ROOT: plainToClass(SerializedNodeDto, ROOT),
      ...Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [key, plainToClass(SerializedNodeDto, value)]),
      ),
    };
    return new CraftJsonDto('ROOT', nodes, false, version);
  }

  /**
   * Create a CraftJsonDto from section format
   * Input: { rootNodeId: string, nodes: {...}, version?: number }
   */
  static fromSection(data: VersionedSectionNodes): CraftJsonDto {
    const { rootNodeId, version, nodes } = data;
    if (!nodes[rootNodeId]) {
      throw new Error(`Root node with id "${rootNodeId}" not found in nodes`);
    }
    const transformedNodes = Object.fromEntries(
      Object.entries(nodes).map(([key, value]) => [key, plainToClass(SerializedNodeDto, value)]),
    );
    return new CraftJsonDto(rootNodeId, transformedNodes, true, version);
  }

  /**
   * Returns JSON string for rendering and wrapping with page root node
   * Output: { ROOT: {...}, nodeId1: {...}, ... }
   */
  forThumbnailRender(pageNode: SerializedNodeDto): string {
    const nodes = Object.fromEntries(
      Object.entries(this.nodes).map(([key, value]) => [key, instanceToPlain(value)]),
    );
    pageNode.nodes = [this.rootNodeId];

    // use background video thumbnail as background image so the thumbnail shows the video poster
    // instead of loading actual video data
    const bgImage = pageNode.props.bgImage;
    const bgVideoThumbnailUrl = pageNode.props.bgVideoThumbnailUrl;
    if (!bgImage && bgVideoThumbnailUrl) {
      pageNode.props.bgVideoEmbedCode = null;
      pageNode.props.bgVideoUrl = null;
      pageNode.props.bgImage = bgVideoThumbnailUrl;
      pageNode.props.bgImageScale = 100;
      pageNode.props.bgImageOpacity = pageNode.props.bgVideoOpacity ?? 100;
      pageNode.props.bgImageMode = (pageNode.props.bgVideoMode ?? 'fill') as BackgroundImageMode;
      pageNode.props.bgImagePosition = 'center center' as BackgroundPosition;
    }

    // keep auto play false incase no thumbnail is available(not sure if there is the case)
    pageNode.props.bgVideoAutoPlay = false;
    return JSON.stringify({ ROOT: pageNode, ...nodes });
  }

  /**
   * Returns JSON string for rendering with ROOT at top level
   * Output: { ROOT: {...}, nodeId1: {...}, ... }
   */
  forRender(): string {
    const nodes = Object.fromEntries(
      Object.entries(this.nodes).map(([key, value]) => [key, instanceToPlain(value)]),
    );
    const rootNode = nodes[this.rootNodeId];
    delete nodes[this.rootNodeId];
    return JSON.stringify({ ROOT: rootNode, ...nodes });
  }

  /**
   * Returns data in the same format it was initialized with
   * - Page format: { ROOT: {...}, nodeId1: {...}, version?: number }
   * - Section format: { rootNodeId: string, nodes: {...}, version?: number }
   */
  forSave(): VersionedSerializedNodes | VersionedSectionNodes {
    if (this.isSectionFormat) {
      return this.toSectionFormat();
    }
    return this.toPageFormat();
  }

  /**
   * Returns full page format
   * Output: { ROOT: {...}, nodeId1: {...}, version?: number }
   */
  toPageFormat(): VersionedSerializedNodes {
    const nodes = Object.fromEntries(
      Object.entries(this.nodes).map(([key, value]) => [
        key,
        instanceToPlain(value) as SerializedNode,
      ]),
    );
    const rootNode = nodes[this.rootNodeId];
    delete nodes[this.rootNodeId];

    const result: VersionedSerializedNodes = { ROOT: rootNode, ...nodes };
    if (this.version !== undefined) {
      result.version = this.version;
    }
    return result;
  }

  /**
   * Returns section format
   * Output: { rootNodeId: string, nodes: {...}, version?: number }
   */
  toSectionFormat(): VersionedSectionNodes {
    const nodes = Object.fromEntries(
      Object.entries(this.nodes).map(([key, value]) => [
        key,
        instanceToPlain(value) as SerializedNode,
      ]),
    );
    const result: VersionedSectionNodes = {
      rootNodeId: this.rootNodeId,
      nodes,
    };
    if (this.version !== undefined) {
      result.version = this.version;
    }
    return result;
  }

  getAllNodes(): Array<{ id: string; node: SerializedNodeDto }> {
    return Object.entries(this.nodes).map(([id, node]) => ({ id, node }));
  }

  /**
   * Get the root node
   */
  get root(): SerializedNodeDto {
    return this.nodes[this.rootNodeId];
  }
}
